import { Container, FormFieldHeading } from '@/components/Elements';
import StepperFormLayout from '@/components/Layout/StepperFormLayout';
import { Button, Col, Row } from 'antd';
import { useForm } from 'react-hook-form';
import useScrollToTop from '@/hooks/useScrollTop';
import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';
import { useNavigate, useParams } from 'react-router-dom';
import { useCountryOptions } from '@/hooks/useCountryOptions';
import { useEffect, useRef, useState } from 'react';
import { refinanceFormRoutes } from '../constant/refinanceStepperRoutes';
import { usePostRefinanceRequestMutation } from '@/pages/mortgage/apis/refinanceAPI';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import { useFetchUserProfileQuery } from '@/store/apis/userApi';
import { PropertyDetailPayload } from '@/pages/mortgage/types/Refinance';
import { PropertyDetailFormType } from '../types/index';
import { propertyDetailLabelMapper } from '../schema/propertyDetailSchema';
import { useGetPersonalPropertyListQuery } from '@/pages/mortgage/apis/propertyAPI';
import PropertyList from '../components/PropertyList';
import { PlusOutlined } from '@ant-design/icons';
import PropertyFormModal from '@/pages/property/components/PropertyFormModal';
import FormMainLabel from '@/components/Elements/Heading/FormMainLabel';

interface Props {
  data: any;
  setValue: any;
  watch: any;
  control: any;
  errors: any;
  unregister: any;
}

const NewPropertyDetail: React.FC<Props> = ({
  data: propertyDetailData,
  setValue,
  watch,
  control,
  errors,
  unregister,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const countryOptions = useCountryOptions();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  useScrollToTop();
  useUnsavedChangesWarning(true);
  const [overseasModal, setOverseasModal] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const {
    data: userProfileData,
    error: userProfileDataError,
    isLoading: userProfileDataLoading,
  } = useFetchUserProfileQuery(undefined, {
    skip: false,
  });

  const { data: propertyData } = useGetPersonalPropertyListQuery({});

  const [australianPropertyData, setAustralianPropertyData] = useState<any[]>(
    [],
  );
  const [overseasPropertyData, setOverseasPropertyData] = useState<any[]>([]);

  useEffect(() => {
    if (propertyData?.data) {
      const australianProperties = propertyData.data.filter(
        (property: any) => property.propertyLocation === 'australian',
      );
      const overseasProperties = propertyData.data.filter(
        (property: any) => property.propertyLocation === 'overseas',
      );
      console.log('australianProperties', australianProperties);
      console.log('overseasProperties', overseasProperties);

      setAustralianPropertyData(australianProperties);
      setOverseasPropertyData(overseasProperties);
    }
  }, [propertyData]);

  const [postRefinanceRequest, { isLoading: postRefinanceRequestLoading }] =
    usePostRefinanceRequestMutation();

  const onSubmit = (data: PropertyDetailFormType) => {
    const payload: PropertyDetailPayload = {
      data,
      step: 'property_detail',
      ...(id ? { application_id: id } : {}),
    };

    postRefinanceRequest(payload)
      .unwrap()
      .then((res) => {
        displaySuccess('Successfully submitted details.');
        navigate(
          `${refinanceFormRoutes['income_detail']}/${res.data.application_id}`,
        );
      })
      .catch((err) => displayError(err));
  };

  const handleFormSubmit = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  useEffect(() => {
    if (id && propertyDetailData) {
      const propertyIds = propertyDetailData?.propertyIds || [];
      const overseasPropertyIds = propertyDetailData?.overseasPropertyIds || [];

      setSelectedProperties(propertyIds);
      setOverseasSelectedProperties(overseasPropertyIds);

      setValue('propertyIds', propertyIds);
      setValue('overseasPropertyIds', overseasPropertyIds);
    }
  }, [propertyDetailData, id]);

  const [selectedProperties, setSelectedProperties] = useState<
    (string | number)[]
  >([]);

  const [overseasSelectedProperties, setOverseasSelectedProperties] = useState<
    (string | number)[]
  >([]);

  const handlePropertySelect = (propertyId: number) => {
    setSelectedProperties((prevSelected) => {
      const updatedSelection = prevSelected.includes(propertyId)
        ? prevSelected.filter((id) => id !== propertyId)
        : [...prevSelected, propertyId];

      setValue('propertyIds', updatedSelection);
      return updatedSelection;
    });
  };

  const handleOverseasPropertySelect = (propertyId: number) => {
    setOverseasSelectedProperties((prevSelected) => {
      const updatedSelection = prevSelected.includes(propertyId)
        ? prevSelected.filter((id) => id !== propertyId)
        : [...prevSelected, propertyId];

      setValue('overseasPropertyIds', updatedSelection);
      return updatedSelection;
    });
  };

  const handleNewPropertyAdded = (newProperty: any) => {
    if (overseasModal) {
      setOverseasSelectedProperties((prevSelected) => {
        const updatedSelection = [...prevSelected, newProperty.id];
        setValue('overseasPropertyIds', updatedSelection);
        return updatedSelection;
      });
    } else {
      setSelectedProperties((prevSelected) => {
        const updatedSelection = [...prevSelected, newProperty.id];
        setValue('propertyIds', updatedSelection);
        return updatedSelection;
      });
    }
  };

  return (
    <Container width="md">
      <Row>
        <Col xs={24}>
          <Row gutter={30}>
            <Col xs={24} md={24}>
              <div className="form-control">
                <FormMainLabel
                  index={7}
                  label={propertyDetailLabelMapper['ausProperty']}
                />
              </div>
            </Col>
            <Col xs={24}>
              <PropertyList
                propertyData={australianPropertyData}
                selectedProperties={selectedProperties}
                showCheckbox={true}
                onSelect={handlePropertySelect}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Button
                  size="large"
                  onClick={() => {
                    setIsModalOpen(true);
                    setOverseasModal(false);
                  }}
                  style={{
                    margin: '1rem 0',
                  }}
                >
                  <PlusOutlined />
                  Add Property
                </Button>
              </div>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col xs={24} md={24}>
              <div className="form-control">
                <FormMainLabel
                  index={8}
                  label={propertyDetailLabelMapper['overseasProperty']}
                />
              </div>
            </Col>
            <Col xs={24}>
              <PropertyList
                propertyData={overseasPropertyData}
                selectedProperties={overseasSelectedProperties}
                showCheckbox={true}
                onSelect={handleOverseasPropertySelect}
                formSource="refinance"
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Button
                  size="large"
                  onClick={() => {
                    setIsModalOpen(true);
                    setOverseasModal(true);
                  }}
                >
                  <PlusOutlined />
                  Add Property
                </Button>
              </div>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col xs={24}>
              <Button
                ref={submitButtonRef}
                type="primary"
                htmlType="submit"
                style={{ display: 'none' }}
              >
                Submit
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <PropertyFormModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        overseas={overseasModal}
        propertyOption="personal"
        onNewPropertyAdded={handleNewPropertyAdded}
        formSource="refinance"
      />
    </Container>
  );
};

export default NewPropertyDetail;
